import { geoEqualEarth, geoGraticule10, geoPath, GeoProjection } from 'd3-geo';
import { feature, mesh } from 'topojson-client';
import type { GeometryCollection, Topology } from 'topojson-specification';
import worldAtlas from 'world-atlas/countries-110m.json';
import {
  denseArcWidth,
  denseDotRadius,
  groupByLocation,
  isDenseMap,
  locationRegion,
  maxLocationCount,
  maxLocationTotal,
} from 'components/TradeTabPanel/denseMode';
import { spreadPartnerDots } from 'components/TradeTabPanel/partnerGeometry';
import { countryNumericId } from './countryNumericIds';
import {
  arcControl22,
  arcPathD,
  arcWidth,
  frameCentreLon,
  frameRect,
  meanLat,
  MIN_LON_SPAN,
  padToMinLonSpan,
  partnerRadius,
  quadraticLength,
  quadraticPoint,
} from './frame';
import { THEME } from './theme';
import { TradeMapModel, TradeMapPartner } from './model';
import { ArcDirection, FrameRect, LonLat, Pt, TradeMapVariant } from './types';

// ONE geometry pass for the profile map (desktop and phone). geoPath() with no
// context returns SVG path data, which the SVG component paints as <path d>.
// d3 lives here and nowhere else in the module. The share card is NOT drawn
// from this: it keeps its own Mercator render (tradeMapCard/cardLayout.ts).

// Natural Earth 1:110m via world-atlas. Decoded once per session.
const world = worldAtlas as unknown as Topology<{ countries: GeometryCollection }>;
const LAND = feature(world, world.objects.countries);
const BORDERS = mesh(world, world.objects.countries, (a, b) => a !== b);
const GRATICULE = geoGraticule10();
const SPHERE = { type: 'Sphere' } as const;

export interface GeometryDot {
  key: string;
  x: number;
  y: number;
  r: number;
  /** In dense mode, the location's sums as one synthesized partner */
  partner: TradeMapPartner;
  /** Dense mode only: what the dot stands for, for the tooltip */
  location?: { partners: number; packages: number };
}

export interface GeometryArc {
  key: string;
  partnerKey: string;
  direction: ArcDirection;
  /** Completed packages in this direction (0 for a live-only partner) */
  count: number;
  /** Packages in the mail in this direction */
  live: number;
  width: number;
  /** Home to partner, always */
  d: string;
  /** The direction the package travels: inbound starts at the partner */
  travelD: string;
  length: number;
  from: Pt;
  to: Pt;
  control: Pt;
  /** t = 0.5 on the quadratic, where the evidence samples the colour */
  mid: Pt;
}

export interface TradeMapGeometry {
  width: number;
  height: number;
  variant: TradeMapVariant;
  frame: FrameRect;
  countries: Array<{ id: string; d: string; traded: boolean }>;
  bordersD: string;
  graticuleD: string;
  sphereD: string;
  home: Pt | null;
  dots: GeometryDot[];
  arcs: GeometryArc[];
  /** Projected width of MIN_LON_SPAN degrees at the frame centre: the zoom clamp, for tests */
  lonSpanPx: number;
  scale: number;
  /** More than DENSE_MAP_PARTNER_THRESHOLD completed partners: one dot per location, no collision */
  dense: boolean;
}

const sphereFit = (extent: [[number, number], [number, number]], centreLon: number): GeoProjection =>
  geoEqualEarth().rotate([-centreLon, 0]).fitExtent(extent, SPHERE);

/**
 * Equal Earth, centred on the owner, framed on the home point plus every
 * placeable partner (addendum 2). The frame inset IS the padding. The 25-degree
 * clamp rides in as two synthetic points (frame.ts), so a single domestic
 * partner reads as a region, never a street. Floored at the whole-sphere fit so
 * a worldwide spread never zooms out past the world and exposes its edge.
 */
export const fitEqualEarth = (points: LonLat[], home: LonLat | null, frame: FrameRect, width: number, height: number): GeoProjection => {
  const extent: [[number, number], [number, number]] = [
    [frame.left, frame.top],
    [frame.right, frame.bottom],
  ];
  const centreLon = frameCentreLon(home, points);
  const everything = home ? [home, ...points] : points;
  const projection =
    everything.length === 0
      ? sphereFit(extent, centreLon)
      : geoEqualEarth()
          .rotate([-centreLon, 0])
          .fitExtent(extent, { type: 'MultiPoint', coordinates: padToMinLonSpan(everything, centreLon, meanLat(everything)) });
  const world = sphereFit(extent, centreLon);
  if (!Number.isFinite(projection.scale()) || projection.scale() < world.scale()) {
    projection.scale(world.scale()).translate(world.translate());
  }
  // Last, so fitExtent's own clip reset never undoes it: off-frame geometry never reaches the DOM
  return projection.clipExtent([
    [0, 0],
    [width, height],
  ]);
};

export const buildTradeMapGeometry = (model: TradeMapModel, variant: TradeMapVariant, width: number, height: number): TradeMapGeometry => {
  const frame = frameRect(variant, width, height);
  const homeLonLat: LonLat | null = model.home ? [model.home.lon, model.home.lat] : null;
  const points: LonLat[] = model.partners.map((p) => [p.lon, p.lat]);
  const projection = fitEqualEarth(points, homeLonLat, frame, width, height);
  const path = geoPath(projection);
  const project = (lon: number, lat: number): Pt => {
    const xy = projection([lon, lat]) ?? [0, 0];
    return { x: xy[0], y: xy[1] };
  };

  const traded = new Set(model.tradedCountries.map((code) => countryNumericId(code)).filter(Boolean) as string[]);
  const countries = LAND.features
    .map((f) => ({ id: String(f.id ?? ''), d: path(f) ?? '', traded: traded.has(String(f.id ?? '')) }))
    .filter((c) => c.d.length > 0);

  const home = homeLonLat ? project(homeLonLat[0], homeLonLat[1]) : null;

  const dots: GeometryDot[] = [];
  const arcs: GeometryArc[] = [];
  // Dense mode (Asana 1218949343743567) is decided on partners with completed
  // packages; in-transit-only partners still render but never count.
  const dense = isDenseMap(model.partners.filter((p) => p.received + p.sent > 0).length);
  if (dense) {
    // One dot per location at its TRUE projected point, no collision, sized by
    // the location's completed package total; one arc per location per
    // direction. The dot carries a synthesized partner (the location's sums)
    // so hover dimming, the live ring, the package animation and the tooltip
    // work unchanged. Group order is total desc, so small dots draw on top.
    const groups = groupByLocation(model.partners, (p) => ({
      key: p.locationKey,
      lon: p.lon,
      lat: p.lat,
      inbound: p.received,
      outbound: p.sent,
      liveInbound: p.liveInbound,
      liveOutbound: p.liveOutbound,
    }));
    const maxTotal = maxLocationTotal(groups);
    const maxCount = maxLocationCount(groups);
    groups.forEach((group) => {
      const to = project(group.lon, group.lat);
      const partner: TradeMapPartner = {
        key: group.key,
        locationKey: group.key,
        region: locationRegion(group.key),
        lon: group.lon,
        lat: group.lat,
        received: group.inbound,
        sent: group.outbound,
        liveInbound: group.liveInbound,
        liveOutbound: group.liveOutbound,
      };
      dots.push({
        key: group.key,
        x: to.x,
        y: to.y,
        r: denseDotRadius(group.total, maxTotal),
        partner,
        location: { partners: group.partners, packages: group.total },
      });
      if (!home) return;
      (['inbound', 'outbound'] as const).forEach((direction) => {
        const count = direction === 'inbound' ? group.inbound : group.outbound;
        const live = direction === 'inbound' ? group.liveInbound : group.liveOutbound;
        if (count <= 0 && live <= 0) return;
        const control = arcControl22(home, to, direction);
        arcs.push({
          key: `${group.key}:${direction}`,
          partnerKey: group.key,
          direction,
          count,
          live,
          width: denseArcWidth(count, maxCount),
          d: arcPathD(home, control, to),
          travelD: direction === 'inbound' ? arcPathD(to, control, home) : arcPathD(home, control, to),
          length: quadraticLength(home, control, to),
          from: home,
          to,
          control,
          mid: quadraticPoint(home, control, to, 0.5),
        });
      });
    });
  } else {
    // The same collision rule as the panel map: the home marker is an obstacle
    // nobody may sit on, and dots are kept inside the frame.
    const inputs = model.partners.map((partner, index) => {
      const r = partnerRadius(partner.received, partner.sent);
      return { id: index, ...project(partner.lon, partner.lat), tradeCount: partner.received + partner.sent, extent: r + THEME.dotStrokeWidth / 2 };
    });
    const placed = spreadPartnerDots(
      inputs,
      width,
      height,
      home ? [{ x: home.x, y: home.y, extent: THEME.homeHaloRadius + 1 }] : [],
      frame
    );
    const positionOf = new Map(placed.map((p) => [p.id, p]));

    model.partners.forEach((partner, index) => {
      const at = positionOf.get(index);
      if (!at) return;
      dots.push({ key: partner.key, x: at.x, y: at.y, r: partnerRadius(partner.received, partner.sent), partner });
      if (!home) return;
      const to = { x: at.x, y: at.y };
      (['inbound', 'outbound'] as const).forEach((direction) => {
        const count = direction === 'inbound' ? partner.received : partner.sent;
        const live = direction === 'inbound' ? partner.liveInbound : partner.liveOutbound;
        if (count <= 0 && live <= 0) return;
        const control = arcControl22(home, to, direction);
        arcs.push({
          key: `${partner.key}:${direction}`,
          partnerKey: partner.key,
          direction,
          count,
          live,
          width: arcWidth(count),
          d: arcPathD(home, control, to),
          // Inbound packages travel partner -> home; the reversed quadratic keeps the same curve
          travelD: direction === 'inbound' ? arcPathD(to, control, home) : arcPathD(home, control, to),
          length: quadraticLength(home, control, to),
          from: home,
          to,
          control,
          mid: quadraticPoint(home, control, to, 0.5),
        });
      });
    });
  }

  const centreLon = frameCentreLon(homeLonLat, points);
  const lat = meanLat(homeLonLat ? [homeLonLat, ...points] : points);
  const spanA = project(centreLon - MIN_LON_SPAN / 2, lat);
  const spanB = project(centreLon + MIN_LON_SPAN / 2, lat);

  return {
    width,
    height,
    variant,
    frame,
    countries,
    bordersD: path(BORDERS) ?? '',
    graticuleD: path(GRATICULE) ?? '',
    sphereD: path(SPHERE) ?? '',
    home,
    dots,
    arcs,
    lonSpanPx: Math.hypot(spanB.x - spanA.x, spanB.y - spanA.y),
    scale: projection.scale(),
    dense,
  };
};
